import Vue from 'vue';
import Vuelidate from 'vuelidate';
import {
  required,
  maxLength
} from 'vuelidate/lib/validators';
import {
  validators,
  $eventBus
} from '../util';
import Imask from 'imask';
import axios from 'axios';

const {
  credit_card_number,
  expiry_date,
  regex
} = validators;

/* global Worldpay, _aspawp, _as_rest */
/* eslint camelcase: 0 */

const {
  client_key: CLIENT_KEY
} = _aspawp;
const {
  rest_url,
  // rest_nonce,
  customer_hash
} = _as_rest;

const CARDS = {
  SUPPORTED_CARDS: {
    maestro: 'Maestro',
    mastercard: 'Mastercard',
    visaelectron: 'Visa Electron',
    visa: 'Visa'
  },
  UNSUPPORTED_CARDS: {
    amex: 'American Express',
    dankort: 'Dankort',
    dinersclub: 'Diners Club',
    discover: 'Discover',
    jcb: 'JCB',
    laser: 'Laser',
    unionpay: 'Union Pay',
    elo: 'Elo'
  }
};
const STATUS = {
  DETAILS_PROCESSING: 'details_processing',
  DETAILS_FAILED: 'details_failed',
  DETAILS_SUCCESS: 'details_success',
  PAYMENT_PROCESSING: 'payment_processing',
  PAYMENT_FAILED: 'payment_failed',
  PAYMENT_SUCCESS: 'payment_success'
};

Vue.use(Vuelidate);
Vue.prototype.$http = axios;

// axios.defaults.headers.common['X-WP-Nonce'] = rest_nonce;
axios.defaults.headers.common['X-Customer-Hash'] = customer_hash;

// eslint-disable-next-line no-new
new Vue({
  el: '#as_payment_world-pay',

  data: {
    order_state: {},
    name: null,
    card_number: null,
    expiry_date: null,
    cvc: null,
    meta: {
      world_pay_loaded: false,
      disable_form: true,
      world_pay_error: false,
      status: {
        processing_details: false
      }
    },
    masks: {}
  },

  validations: {
    name: {
      required,
      maxLength: maxLength(255)
    },
    card_number: {
      required,
      credit_card_number
    },
    expiry_date: {
      required,
      regex: regex(/^(0[1-9]|1[012])\/[0-9]{2}$/),
      expiry_date
    },
    cvc: {
      required,
      regex: regex(/^[0-9]{3}$/)
    }
  },

  created() {
    this.update_world_pay_loaded();
  },

  mounted() {
    $eventBus.$emit('mounted', this);
  },

  computed: {
    card_type_from_number() {
      if (!this.meta.world_pay_loaded) {
        return null;
      }

      const card = Worldpay.card.cardFromNumber(this.card_number);
      const type = card && card.type ? card.type : null;

      if (!CARDS.SUPPORTED_CARDS[type] && !CARDS.UNSUPPORTED_CARDS[type]) {
        return null;
      }

      return type;
    },
    card_from_number() {
      if (this.card_type_from_number) {
        return CARDS.UNSUPPORTED_CARDS[this.card_type_from_number] || CARDS.SUPPORTED_CARDS[this.card_type_from_number];
      }
    },
    is_card_supported() {
      return Boolean(CARDS.SUPPORTED_CARDS[this.card_type_from_number]);
    },
    $status: {
      get() {
        return {
          details_processing: this.meta.status === STATUS.DETAILS_PROCESSING,
          details_failed: this.meta.status === STATUS.DETAILS_FAILED,
          details_success: this.meta.status === STATUS.DETAILS_SUCCESS,
          payment_processing: this.meta.status === STATUS.PAYMENT_PROCESSING,
          payment_failed: this.meta.status === STATUS.PAYMENT_FAILED,
          payment_success: this.meta.status === STATUS.PAYMENT_SUCCESS
        };
      }
    }
  },

  watch: {
    'meta.world_pay_loaded'(value) {
      if (value) {
        this.meta.disable_form = false;
      }
    }
  },

  methods: {
    update_world_pay_loaded() {
      // eslint-disable-next-line yoda
      if (typeof Worldpay === 'undefined') {
        return setTimeout(() => {
          this.update_world_pay_loaded();
        }, 100);
      }

      this.meta.world_pay_loaded = true;
    },
    update_card_number() {
      setTimeout(() => {
        this.card_number = this.masks.card_number.unmaskedValue;
      }, 1);
    },
    update_expiry_date() {
      setTimeout(() => {
        this.expiry_date = this.masks.expiry_date.value;
      }, 1);
    },
    update_cvc() {
      setTimeout(() => {
        this.cvc = this.masks.cvc.value;
      }, 1);
    },

    initialise_mask(field) {
      let initialised = false;

      if ('card_number' === field && !this.masks.card_number && this.$refs.card_number) {
        this.masks.card_number = new Imask(this.$refs.card_number, {
          mask: '0000 0000 0000 0000'
        });
        initialised = true;
      }

      if ('expiry_date' === field && !this.masks.expiry_date && this.$refs.expiry_date) {
        this.masks.expiry_date = new Imask(this.$refs.expiry_date, {
          mask: '00/00'
        });
        initialised = true;
      }

      if ('cvc' === field && !this.masks.cvc && this.$refs.cvc) {
        this.masks.cvc = new Imask(this.$refs.cvc, {
          mask: '000'
        });
        initialised = true;
      }

      return initialised;
    },

    pay() {
      this.$v.name.$touch();
      this.$v.card_number.$touch();
      this.$v.expiry_date.$touch();
      this.$v.cvc.$touch();

      const {
        order: {
          id: orderId = false
        } = {}
      } = this.order_state;

      if (!orderId) {
        this.meta.world_pay_error = 'Order id can not be found. Please refresh the page';
        this.meta.status = STATUS.DETAILS_FAILED;
      }

      if (this.$v.invalid || !this.is_card_supported || !orderId) {
        $eventBus.$emit('has-error');

        return;
      }

      this.meta.disable_form = true;
      this.meta.world_pay_error = false;
      this.meta.status = STATUS.DETAILS_PROCESSING;

      this
        .$http({
          method: 'POST',
          url: `${Worldpay.api_path}tokens`,
          data: {
            reusable: true,
            paymentMethod: {
              type: 'Card',
              name: this.name,
              expiryMonth: this.expiry_date.replace(/\/[0-9]{2}$/, ''),
              expiryYear: this.expiry_date.replace(/^[0-9]{2}\//, '20'),
              cardNumber: this.card_number,
              cvc: this.cvc
            },
            clientKey: CLIENT_KEY
          },
          transformRequest: [function(data, headers) {
            // delete headers.common['X-WP-Nonce'];
            delete headers.common['X-Customer-Hash'];
            headers['Content-Type'] = 'application/json';

            return JSON.stringify(data);
          }]
        })
        .catch(error => {
          this.meta.disable_form = false;
          this.meta.world_pay_error = error.message || 'Error processing card details';
          this.meta.status = STATUS.DETAILS_FAILED;
        })
        .then(({ data: { token, reusable } }) => {
          this.meta.status = STATUS.DETAILS_SUCCESS;
          this.meta.status = STATUS.PAYMENT_PROCESSING;

          return this
            .$http({
              method: 'POST',
              url: `${rest_url}wp-asurit-sales/v1/orders/${orderId}/payments`,
              data: {
                type: 'credit-card',
                data: {
                  token,
                  reusable,
                  name: this.name,
                  type: this.card_from_number,
                  expiry: {
                    month: this.expiry_date.replace(/\/[0-9]{2}$/, ''),
                    year: this.expiry_date.replace(/^[0-9]{2}\//, '20')
                  },
                  number: this.card_number.substr(-4)
                }
              }
            })
          ;
        })
        .then(({ data: { payments = {} } }) => {
          this.meta.status = STATUS.PAYMENT_SUCCESS;
          this.meta.disable_form = false;

          let emit = {
            completed: 'payment',
            payments: {
              ...payments
            }
          };

          $eventBus.$emit('order-updated', emit);
        })
        .catch(error => {
          let data = error.response && error.response.data || { message: 'Error processing payment' };

          this.meta.status = STATUS.PAYMENT_FAILED;
          this.meta.disable_form = false;
          this.meta.world_pay_error = data.message;
        })
      ;
    },
    edit() {
      $eventBus.$emit('order-updated', { edit: 'payment' });
    }
  }
});
